import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative overflow-hidden",
  {
    variants: {
      variant: {
        default: "gradient-primary text-primary-foreground hover:scale-105 hover:shadow-primary transition-elastic shadow-card",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:scale-105 transition-elastic shadow-card",
        outline: "border border-border glass hover:glass-strong hover:border-primary/50 text-foreground hover:scale-[1.02] transition-smooth",
        secondary: "gradient-cosmic bg-secondary/20 text-secondary-foreground hover:bg-secondary/30 hover:scale-105 transition-elastic border border-secondary/30",
        ghost: "hover:glass hover:scale-[1.02] text-muted-foreground hover:text-foreground transition-smooth",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-glow transition-smooth",
        premium: "gradient-primary text-primary-foreground hover:scale-105 hover:glow-primary transition-elastic shadow-elevated animate-shimmer bg-gradient-to-r from-primary via-secondary to-tertiary bg-[length:200%_100%]",
        tertiary: "bg-tertiary text-tertiary-foreground hover:bg-tertiary/90 hover:scale-105 hover:glow-tertiary transition-elastic shadow-tertiary",
        success: "bg-success text-success-foreground hover:bg-success/90 hover:scale-105 hover:glow-green transition-elastic",
        floating: "glass hover:glass-strong hover:scale-105 hover:shadow-floating hover:-translate-y-1 transition-elastic backdrop-blur-xl",
      },
      size: {
        default: "h-12 px-6 py-3 text-sm",
        sm: "h-9 text-xs px-3 sm:h-10 sm:text-sm sm:px-4",
        lg: "h-14 rounded-xl px-8 text-base font-semibold",
        icon: "h-12 w-12 rounded-xl",
        xl: "h-16 rounded-2xl px-10 text-lg font-bold",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
